import type { AnchorHTMLAttributes, ImgHTMLAttributes } from 'react'
import type { ExtraProps } from 'react-markdown'

import { isNavigableHref, isSafeImageDataContentType } from '@/utils/markdownUrl'

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & ExtraProps
type ImageProps = ImgHTMLAttributes<HTMLImageElement> & ExtraProps

/**
 * Link renderer for chat markdown. A link whose URL was stripped as unsafe (such as a Code
 * Interpreter `sandbox:` link) is shown as plain text: rendered as `<a href="" target="_blank">` it
 * reopened the current session in a new tab.
 */
export function MarkdownLink({ node: _, href, children, ...props }: LinkProps) {
  if (!isNavigableHref(href)) {
    return <span data-component="markdown__unlinked-text">{children}</span>
  }
  // In-page fragments stay in this tab.
  if (href.startsWith('#')) {
    return (
      <a href={href} {...props}>
        {children}
      </a>
    )
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
      {children}
    </a>
  )
}

/** Image renderer for chat markdown: unsafe data URLs and stripped sources never hit the network. */
export function MarkdownImage({ node: _, ...props }: ImageProps) {
  const alt = typeof props.alt === 'string' ? props.alt : 'unknown image'
  if (typeof props.src === 'string' && props.src.startsWith('data:')) {
    const contentType = props.src.split(';')[0].split(':')[1]
    if (!isSafeImageDataContentType(contentType)) {
      return <img {...props} src="about:blank" alt={`Unsupported image format: ${contentType}`} />
    }
  }
  // An empty src would request the current page; show the alt text instead.
  if (!isNavigableHref(typeof props.src === 'string' ? props.src : undefined)) {
    return <span data-component="markdown__unlinked-text">{alt}</span>
  }
  return <img {...props} alt={alt} />
}
